import React, { useState, useEffect } from 'react';
import { Chip, Box, CircularProgress, Dialog, IconButton } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { filesApi } from '../services/api';

const FileAttachment = ({ attachment }) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImage, setIsImage] = useState(false);
  const [isAudio, setIsAudio] = useState(false);
  const [fileName, setFileName] = useState(attachment.originalName || attachment.filename || '');
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    const fetchFileUrl = async () => {
      try {
        const response = await filesApi.getFileUrl(attachment.id);
        setFileUrl(response.data.url);
        
        // Сохраняем имя файла из ответа API
        if (response.data.filename) {
          setFileName(response.data.filename);
        }
        
        // Проверяем, является ли файл изображением или аудио
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
        const audioExtensions = ['.mp3', '.m4a', '.aac', '.wav', '.ogg', '.oga', '.opus', '.flac', '.webm'];
        const displayFileName = response.data.filename || attachment.originalName || attachment.filename || '';
        const ext = `.${displayFileName.toLowerCase().split('.').pop()}`;
        setIsImage(imageExtensions.includes(ext));
        setIsAudio(audioExtensions.includes(ext));
      } catch (error) {
        console.error('Error fetching file URL:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFileUrl();
  }, [attachment.id]);

  if (loading) {
    return (
      <Chip
        label={attachment.originalName || fileName || attachment.filename || 'Файл'}
        icon={<CircularProgress size={24} />}
        disabled
      />
    );
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-block', m: 1 }}>
      {/* Для обычных файлов показываем чип */}
      {!isAudio && !isImage && (
        <Chip
          label={attachment.originalName || fileName || attachment.filename || 'Файл'}
          icon={<AttachFileIcon />}
          component="a"
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          clickable
          sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
        />
      )}
      {/* Аудио — только плеер */}
      {isAudio && fileUrl && (
        <Box sx={{ mt: 1 }}>
          <audio controls preload="none" style={{ width: 260 }}>
            <source src={fileUrl} />
            Ваш браузер не поддерживает воспроизведение аудио.
          </audio>
        </Box>
      )}
      {/* Изображения — мини-превью и полноэкранный просмотр по клику */}
      {isImage && fileUrl && (
        <>
          <Box
            component="img"
            src={fileUrl}
            alt={(attachment.filename && decodeURIComponent(attachment.filename)) || fileName}
            onClick={() => setPreviewOpen(true)}
            sx={{
              width: 260,
              height: 160,
              objectFit: 'cover',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              cursor: 'zoom-in',
              display: 'block'
            }}
          />
          <Dialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            fullScreen
            PaperProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.9)' } }}
          >
            <IconButton
              aria-label="Закрыть"
              onClick={() => setPreviewOpen(false)}
              sx={{ position: 'fixed', top: 12, right: 12, color: '#fff', zIndex: 1 }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              sx={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
              }}
              onClick={() => setPreviewOpen(false)}
            >
              <Box
                component="img"
                src={fileUrl}
                alt={(attachment.filename && decodeURIComponent(attachment.filename)) || fileName}
                sx={{
                  maxWidth: '95vw',
                  maxHeight: '95vh',
                  objectFit: 'contain',
                  boxShadow: 3,
                  borderRadius: 1
                }}
              />
            </Box>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default FileAttachment;
